import { TDynamicFormConfig } from "@/features/Edit/dynamic-edit-form";
import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";
import { Control } from "react-hook-form";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

import { AutocompleteAsyncField } from "@/features/FormCard/Fields";
import {
  Option,
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER,
} from "../../../features/Edit/common";
import { z } from "zod";

function ConcentratedAssetField(control: Control<any>) {
  return (
    <AutocompleteAsyncField
      name="concentrated_asset"
      label="Actif concentré du client (indiqué le code client)"
      control={control}
      base_url={`${Clients.S3}/${Tables.S3_PORTFOLIOS}`}
      column_name="client"
    />
  );
}

const concentrated_asset_conf = {
  label: "Actif concentré du client",
  validator: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string().nullable().optional(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
          account_type: z.string(),
          currency: z.string(),
        }),
      ]),
    )
    .optional()
    .nullable(),
  field: ConcentratedAssetField,
};

// Example of a dynamic form config
export const dynamicFormConfig: TDynamicFormConfig = {
  id: z.number(),
  name: z
    .string()
    .min(1, "Le nom est obligatoire")
    .transform((val) => val.toUpperCase()),
  isin: z
    .string()
    .refine(
      (val) => val === "" || val === null || val.length === 12,
      "L'ISIN doit comporter  12 caractères",
    )
    .nullable()
    .optional(),
  code: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  issuer_id: z.coerce.number().optional().nullable(),
  s3_issuer_name: z
    .preprocess(
      preprocessTextAsyncAutocompleteField,
      z.union([
        // we recv / send as string the data
        z.string().nullable().optional(),

        // for the autocomplete, the form state is an object
        z.object({
          text: z.string(),
          id: z.number(),
        }),
      ]),
    )
    .optional(),
  // s3_issuer_name: z.string().nullable().optional(),
  strategy_id: z.coerce.number().optional().nullable(),
  asset_class_2: {
    key_config: {
      validator: {
        asset_class_1: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      },
      primary_key: "asset_class_1",
      secondary_key: "asset_class_2",
    },
    select: {
      equity: {
        stock: "stock",
        stock_fund: false,
        structured_product: "structured_product",
        stock_option: "stock_option",
      },
      art: {
        art: false,
        jewel: false,
        vehicle: "vehicle",
      },
      alternative: {
        crypto: false,
        hedge_fund: false,
        hedge_fund_unlisted: false,
        precious_metal: false,
        other_alternative_listed: false,
        other_alternative_non_listed: false,
      },
      real_estate: {
        re_fund: false,
        building: "building",
        personal_use_real_estate: "personal_use_real_estate",
        scpi_usufruit: "scpi_usufruit",
      },
      cash: {
        cash: "cash",
        fonds_euros: false,
        monetary_fund: false,
      },
      fixed_income: {
        private_debt: "private_debt",
        relatives_debt: "relatives_debt",
        fixed_income_fund: false,
        bond: "bond",
        fixed_income_structured_product: "fixed_income_structured_product",
      },
      loan: {
        home_loan: "home_loan",
        other_loan: "other_loan",
      },
      private_equity: {
        private_equity_fund: "private_equity_fund",
        private_equity_direct: false,
      },
      diversified_investments: {
        diversified_fund: "diversified_fund",
      },
    },
    section: {
      stock: {
        concentrated_asset: concentrated_asset_conf,
      },
      // Other loan
      other_loan: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },

        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },

        loan_amount_granted: {
          label: "Montant du prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_with_collateral: {
          label: "Prêt consenti avec nantissement",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Cash
      cash: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        concentrated_asset: concentrated_asset_conf,
      },
      // Private debt
      private_debt: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // PE fund
      private_equity_fund: {
        commitment: {
          label: "Engagement Initial",
          validator: z.coerce.number().optional().nullable(),
        },
        capital_callable: {
          label: "Capital Exigible",
          validator: z.boolean().optional().nullable(),
        },
      },
      // Building
      building: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Personal use real estate
      personal_use_real_estate: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_type: {
          label: "Type de bien",
          validator: z.string().optional().nullable(),
        },
        property_size: {
          label: "Taille du bien",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_own_funds: {
          label: "Financement fonds propres",
          validator: z.coerce.number().optional().nullable(),
        },
        financing_mortage: {
          label: "Financement hypothécaire",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Bonds
      bond: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Home loan
      home_loan: {
        owner: {
          label: "Propriétaire",
          validator: z.string().optional().nullable(),
        },
        property_id: {
          label: "ID de la propriété",
          validator: z.coerce.number().optional().nullable(),
        },
        loan_amount_granted: {
          label: "Montant prêt accordé",
          validator: z.coerce.number().optional().nullable(),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
      },
      // Family loans
      relatives_debt: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
        coupon_rate: {
          label: "Taux du coupon",
          validator: z.coerce.number().optional().nullable(),
        },
      },
      // Structured products
      structured_product: {
        maturity_date: {
          label: "Date de maturité",
          validator: z
            .preprocess((val) => parseToDateValue(val as string), dateSchema)
            .optional()
            .nullable()
            .describe("date"),
        },
      },
      // Stock options
      stock_option: {
        stock_option_strike: {
          label: "Prix d'exercice",
          validator: z.coerce.number().optional().nullable(),
        },
        concentrated_asset: concentrated_asset_conf,
      },
    },
  },
};

export const DEFAULT_ASSET_CLASS_OPTIONS: Option[] =
  DEFAULT_ASSET_CLASS_OPTIONS_BUILDER(dynamicFormConfig);

export const dynamicNewFormConfigWithoutId: Omit<TDynamicFormConfig, "id"> =
  (() => {
    const { id, ...configWithoutId } = dynamicFormConfig;
    return configWithoutId;
  })();
